import { track } from '@eventbrite/datalayer-library';
import isUndefined from 'lodash/isUndefined';

import { ISocialLogInApiResponseProps } from '../api/social';

import {
    HEAP_EVENT_NAME_SIGNUP_SUCCESSFUL,
    HEAP_EVENT_NAME_WEB_SIGNIN_LOGIN_SUCCESSFUL,
    HEAP_SOCIAL_LOGIN_ERROR,
    LOGIN_METHOD_APPLE,
    LOGIN_METHOD_EVENTBRITE,
    LOGIN_METHOD_FACEBOOK,
    LOGIN_METHOD_GOOGLE,
    SIGNUP_TYPE_SIGNUP_PAGE,
} from '../constants/analytics';

import { logEvent } from '@eventbrite/statsig';
import { capitalize } from './utils';

/**
Tracks LoginSingup metrics in Heap and Statsig
*/
export const trackLoginAndSignUpSuccessInHeap = ({
    loginMethod,
    signupType,
}: {
    loginMethod?: string;
    signupType?: string;
}) => {
    let signupMethod = loginMethod || 'Email';
    signupMethod = signupMethod === 'Eventbrite' ? 'Email' : signupMethod;

    track({
        eventName: HEAP_EVENT_NAME_SIGNUP_SUCCESSFUL,
        eventData: {
            signupMethod,
            signupType,
        },
    });

    trackLoginSuccessInHeap({ loginMethod: loginMethod, signup: true });
};

/**
 *
 * Central utility for Tracking WebSignInLoginSuccessful events
 * for Heap and Statsig
 */
export const trackLoginSuccessInHeap = ({
    loginMethod,
    signup,
}: {
    loginMethod?: string;
    signup?: boolean | null;
}) => {
    track({
        eventName: HEAP_EVENT_NAME_WEB_SIGNIN_LOGIN_SUCCESSFUL,
        eventData: {
            loginMethod: !isUndefined(loginMethod)
                ? capitalize(loginMethod)
                : LOGIN_METHOD_EVENTBRITE,
            isSignUp: signup ? true : false,
        },
    });
    logEvent(HEAP_EVENT_NAME_WEB_SIGNIN_LOGIN_SUCCESSFUL, null, {
        loginMethod: !isUndefined(loginMethod)
            ? capitalize(loginMethod)
            : LOGIN_METHOD_EVENTBRITE,
        isSignUp: signup ? 'true' : 'false',
    });
};

/**
 Tracks FB Login metrics for Heap and Statsig
 */
export const trackFBLoginSuccessInHeap = ({
    response,
}: {
    response: ISocialLogInApiResponseProps;
}) => {
    if (response.newUser) {
        trackLoginAndSignUpSuccessInHeap({
            loginMethod: LOGIN_METHOD_FACEBOOK,
            signupType: SIGNUP_TYPE_SIGNUP_PAGE,
        });
        logEvent(HEAP_EVENT_NAME_SIGNUP_SUCCESSFUL, null, {
            loginMethod: LOGIN_METHOD_FACEBOOK,
            isSignup: 'true',
        });
    } else {
        // One step login
    }
};

/**
 Tracks Apple Login metrics for Heap and Statsig
 */
export const trackAppleLoginSuccessInHeap = ({
    response,
}: {
    response: any;
}) => {
    if (response.newUser ?? response['new_user']) {
        trackLoginAndSignUpSuccessInHeap({
            loginMethod: LOGIN_METHOD_APPLE,
            signupType: SIGNUP_TYPE_SIGNUP_PAGE,
        });
        logEvent(HEAP_EVENT_NAME_SIGNUP_SUCCESSFUL, null, {
            loginMethod: LOGIN_METHOD_APPLE,
            isSignup: 'true',
        });
    } else {
        // One step login
    }
};

/**
 Tracks Google Login metrics for Heap and Statsig
 */
export const trackGoogleLoginSuccess = ({ response }: { response: any }) => {
    if (response['new_user']) {
        trackLoginAndSignUpSuccessInHeap({
            loginMethod: LOGIN_METHOD_GOOGLE,
            signupType: SIGNUP_TYPE_SIGNUP_PAGE,
        });
        logEvent(HEAP_EVENT_NAME_SIGNUP_SUCCESSFUL, null, {
            loginMethod: LOGIN_METHOD_GOOGLE,
            isSignup: 'true',
        });
    } else {
        // One step login
    }
};

export const trackPasswordlessEmail = ({
    eventName,
    pagePath,
}: {
    eventName: string;
    pagePath: string;
}) => {
    track({
        eventName,
        eventData: {
            pagePath,
        },
    });
};

export const trackSocialLoginErrorHeap = ({
    pagePath,
    socialMethod,
    errorType,
}: {
    pagePath: string;
    socialMethod: string;
    errorType: string;
}) => {
    track({
        eventName: HEAP_SOCIAL_LOGIN_ERROR,
        eventData: {
            pagePath,
            socialMethod: capitalize(socialMethod),
            errorType: errorType,
        },
    });
};

export const trackResetPasswordHeap = ({
    eventName,
    pagePath,
    errorType,
}: {
    eventName: string;
    pagePath: string;
    errorType?: string;
}) => {
    track({
        eventName,
        eventData: {
            pagePath,
            ...(errorType ? { errorType } : {}),
        },
    });
};

export const trackAccountLinkingUnlinkingHeap = ({
    eventName,
    socialMethod,
    linkingType,
    errorType,
}: {
    eventName: string;
    socialMethod: string;
    linkingType: string;
    errorType?: string;
}) => {
    track({
        eventName,
        eventData: {
            socialMethod,
            linkingType,
            ...(errorType ? { errorType } : {}),
        },
    });
};
